import { debounce } from 'throttle-debounce'
import { MicrophoneManager } from '../shared/microphone_manager.js'
import { TranscriptionEvents } from './events.js'
import { OpenAIAdapter } from './adapters/openai_adapter.js'

/**
 * TranscriptionService - Main orchestrator for voice transcription functionality
 */
export class TranscriptionService {
  constructor(options = {}) {
    this.options = { adapter: 'openai', ...options }

    this.events = new TranscriptionEvents()
    this.adapter = null
    this.isActive = false
  }

  // Public API

  /**
   * Start voice transcription
   * @returns {Promise<void>}
   */
  async start() {
    if (this.isActive) {
      throw new Error('Transcription is already active')
    }

    try {
      // Initialize components if not already done
      await this.initializeAdapter()

      // Request microphone permission
      this.emit(TranscriptionEvents.EVENTS.PERMISSION_REQUESTED)
      const hasPermission = await MicrophoneManager.requestPermission()

      if (!hasPermission) {
        this.emit(TranscriptionEvents.EVENTS.PERMISSION_DENIED)
        throw new Error('Microphone permission denied')
      }

      this.emit(TranscriptionEvents.EVENTS.PERMISSION_GRANTED)

      // Update status to connecting after permission granted
      this.emit(TranscriptionEvents.EVENTS.CONNECTION_CONNECTING)

      // Connect to transcription service with media stream helper
      const getMediaStream = () => MicrophoneManager.acquire()
      await this.adapter.connect(getMediaStream)

      this.isActive = true

      if (this.disconnectTimeout) {
        clearTimeout(this.disconnectTimeout)
      }
      this.disconnectTimeout = setTimeout(() => {
        this.disconnectTimeout = null
        this.stop()
      }, 1000 * 60 * 25) // Disconnect in 25 minutes (max is 30 minutes)
    } catch (error) {
      await this.cleanup()
      this.handleError(error)
      throw error
    }
  }

  /**
   * Stop voice transcription
   * @returns {Promise<void>}
   */
  async stop() {
    if (!this.isActive) return

    try {
      await this.cleanup()
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Check if transcription is currently active
   * @returns {boolean}
   */
  isActive() {
    return this.isActive
  }

  // Configuration methods

  on(event, callback) {
    this.events.on(event, callback)
  }

  off(event, callback) {
    this.events.off(event, callback)
  }

  emit(event, data) {
    this.events.emit(event, data)
  }

  emitToDoc(event) {
    document.dispatchEvent(new CustomEvent(event, { detail: { source: this } }))
  }

  // Private methods

  async initializeAdapter() {
    if (this.adapter) return

    try {
      // Dynamic import based on adapter
      let AdapterClass
      switch (this.options.adapter) {
        case 'openai':
          AdapterClass = OpenAIAdapter
          break
        // Add more adapters here in the future
        default:
          throw new Error(`Unsupported adapter: ${this.options.adapter}`)
      }

      this.adapter = new AdapterClass()

      // Forward events
      ;[
        TranscriptionEvents.EVENTS.CONNECTION_ESTABLISHED,
        TranscriptionEvents.EVENTS.CONNECTION_LOST,
        TranscriptionEvents.EVENTS.TRANSCRIPTION_STARTED,
        TranscriptionEvents.EVENTS.TRANSCRIPTION_PROCESSING,
        TranscriptionEvents.EVENTS.TRANSCRIPTION_PROCESSED,
      ].forEach((event) => {
        this.adapter.on(event, () => {
          this.emitToDoc(event)
          this.emit(event)
        })
      })

      this.adapter.on(TranscriptionEvents.EVENTS.TRANSCRIPTION_UPDATE, (data) => {
        this.emitToDoc(TranscriptionEvents.EVENTS.TRANSCRIPTION_UPDATE)
        this.handleTranscriptionResponse(data)
      })

      this.adapter.on(TranscriptionEvents.EVENTS.TRANSCRIPTION_ERROR, (error) => this.handleError(error))
    } catch (error) {
      throw new Error(`Failed to initialize adapter: ${error.message}`)
    }
  }

  handleTranscriptionResponse(response) {
    if (!this.isActive) return

    try {
      this.emit(TranscriptionEvents.EVENTS.TRANSCRIPTION_UPDATE, response)
    } catch (error) {
      this.handleError(error)
    }
  }

  handleError(error) {
    console.error('Voice transcription service error:', error)
    this.emit(TranscriptionEvents.EVENTS.TRANSCRIPTION_ERROR, error)
    alert(error.message)

    // Auto-stop on error to clean up resources
    if (this.isActive) {
      this.stop().catch((stopError) => {
        console.error('Error stopping transcription after error:', stopError)
      })
    }
  }

  async cleanup() {
    this.isActive = false

    // Release microphone reference
    try {
      await MicrophoneManager.release()
    } catch (error) {
      console.warn('Error releasing microphone reference:', error)
    }

    // Cleanup adapter
    if (this.adapter) {
      try {
        await this.adapter.disconnect()
      } catch (error) {
        console.warn('Error cleaning up adapter:', error)
      }
      // Don't null out adapter - we can reuse it
    }

    this.emitToDoc(TranscriptionEvents.EVENTS.TRANSCRIPTION_ENDED)
    this.emit(TranscriptionEvents.EVENTS.TRANSCRIPTION_ENDED)
  }
}
